import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { PATH_MISSION_REPORT_PRINTER } from '@e-socna/printer-contract';

import { useReportInfoAccess } from '../../components/Access/hooks/useReportInfoAccess.hook';
import {
  PATH_MISSION_DEFAULT,
  PATH_MISSION_REPORT_DEFAULT,
  PATH_MISSION_REPORT_EDITOR,
  PATH_MISSION_REPORT_GENERATION,
  PATH_MISSION_REPORT_INFO,
  PATH_MISSION_REPORT_INFO_EDIT,
  PATH_MISSION_REPORT_INFO_EDIT_STATE,
  PATH_MISSION_REPORT_PREVIEW,
  PATH_MISSION_REPORT_SHARE,
  PATH_MISSION_REPORT_SUMMARY,
  PATH_MISSION_REPORT_VERSIONS,
  PATH_MISSION_REPORTS,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { useMissionBreadcrumbPaths } from '../../hooks/useMissionBreadcrumbPaths.hook';
import { useMissionSpaceWording } from '../../hooks/useMissionSpaceWording.hook';
import { DocumentEditor } from '../../components/DocumentEditor';
import { Loading } from '../../components/Loading';
import { FormVariant } from '../../forms';
import { BackupMode } from '../../components/DocumentEditor/enums/backup-mode.enum';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { useReportReadyContext } from '../../contexts/ReportContext/hooks/useReportReadyContext.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { useGetters } from './hooks/useGetters.hook';
import { MissionReportInfoTabPanel } from './Info';
import { useSaveHandler } from './hooks/useSaveHandler.hook';
import { useSavePreviewHandler } from './hooks/useSavePreviewHandler.hook';
import { usePrintHandler } from './hooks/usePrintHandler.hook';
import { useSavePrintSettingsHandler } from './hooks/useSavePrintSettingsHandler.hook';

interface Props {
  readonly isInPrintMode: boolean;
}

const NO_TEMPLATE_ID = 'no-template';

export const MissionReportReady = ({ isInPrintMode }: Readonly<Props>) => {
  const { folderId, missionId, mission } = useMissionReadyContext();
  const missionBreadcrumbPaths = useMissionBreadcrumbPaths();
  const missionWording = useMissionSpaceWording();
  const { missionReportId, missionReport, missionReportInfo } =
    useReportReadyContext();
  const access = useReportInfoAccess(missionId, missionReportInfo._id, mission);

  const [isLoading, setIsLoading] = useState(false);

  const { paths: missionPaths } = useBuildPaths(
    {
      default: PATH_MISSION_DEFAULT,
      reports: PATH_MISSION_REPORTS,
      report: PATH_MISSION_REPORT_DEFAULT,
    },
    { folderId, missionId, reportInfoId: missionReportInfo._id },
  );

  const { paths: editorModePaths } = useBuildPaths(
    {
      Info: PATH_MISSION_REPORT_INFO,
      InfoEdit: PATH_MISSION_REPORT_INFO_EDIT,
      InfoEditState: PATH_MISSION_REPORT_INFO_EDIT_STATE,
      Editor: PATH_MISSION_REPORT_EDITOR,
      Printer: PATH_MISSION_REPORT_PRINTER,
      Summary: PATH_MISSION_REPORT_SUMMARY,
      Preview: PATH_MISSION_REPORT_PREVIEW,
      Generation: PATH_MISSION_REPORT_GENERATION,
      Versions: PATH_MISSION_REPORT_VERSIONS,
      Share: PATH_MISSION_REPORT_SHARE,
    },
    { folderId, missionId, reportInfoId: missionReportInfo._id },
  );

  const { onSave } = useSaveHandler({
    currentInfo: missionReportInfo,
    editorModePaths,
    missionReportId,
    setIsLoading,
  });

  const { onSavePreview } = useSavePreviewHandler({
    missionReportId,
    setIsLoading,
  });

  const { onPrint } = usePrintHandler({
    missionReportId,
    snackbarActionTarget: missionPaths.report,
  });

  const { onSavePrintSettings } = useSavePrintSettingsHandler({
    missionReportId,
  });

  const getters = useGetters();

  const reportWithItsInfo = {
    ...missionReport,
    info: missionReportInfo,
  };

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!isLoading && <Loading showBackdrop={false} variant="linear" />}
      {!isInPrintMode && (
        <>
          <Helmet>
            <title>
              {buildDocumentTitle(
                `Fiche Rapport ${missionWording.ofName}`,
                missionReport.code,
              )}
            </title>
          </Helmet>
          <Breadcrumbs
            paths={[
              ...missionBreadcrumbPaths,
              {
                to: missionPaths.reports,
                label: 'rapports',
                key: 'reports',
              },
              {
                to: missionPaths.report,
                label: 'rapport',
                key: 'report',
              },
            ]}
          />
          <PageTitle
            code={missionReport.code}
            title={missionReport.title ?? missionReport.code}
          />
        </>
      )}
      <DocumentEditor
        componentSettings={{
          InfoTabPanel: MissionReportInfoTabPanel,
          welcomeMessage:
            'Votre rapport est vide pour le moment. Commencez le travail !',
        }}
        formSettings={{
          formVariant: FormVariant.Modification,
          initialEditable: reportWithItsInfo,
          isLoading,
        }}
        getters={getters}
        handlers={{
          onSavePreview,
          onSave,
          onPrint,
          onSavePrintSettings,
        }}
        reinitKey={`${missionReportId}-${missionReport.templateId ?? NO_TEMPLATE_ID}`}
        resolversSettings={{
          allowedReturnTypeIds: undefined,
          enableDirectGeneration: true,
          mustBeResolvableOnBack: false,
        }}
        routerSettings={{
          editorModePaths,
          gates: {
            infoEdit: access.update,
            infoEditState: access.changeState,
            contentEdit: access.updateDocument,
          },
          isInPrintMode,
        }}
        saveSettings={{
          backupMode: BackupMode.Auto,
        }}
      />
    </>
  );
};
